import { useState, useEffect } from 'react';
import vaultPlayLogo from '@/assets/brand/vault-play-logo.png';

interface SplashScreenProps {
  /** Whether the app is ready (config loaded) */
  isReady: boolean;
  /** Callback when fade-out animation completes */
  onFinished: () => void;
  /** Maximum time to show splash (ms) - safety timeout */
  maxDuration?: number;
  /**
   * Where to render the overlay.
   * - fullscreen: covers the whole viewport (default)
   * - container: covers only the nearest positioned ancestor
   */
  scope?: 'fullscreen' | 'container';
}

/**
 * Premium Casino-style Splash Screen
 * Shown while VFX config is loading to prevent theme flash.
 * Colours come from the v2 tokens (base, tint glow, primary action, beam); it sits on the
 * splash layer (--k-z-splash), the highest one.
 */
export default function SplashScreen({
  isReady,
  onFinished,
  maxDuration = 8000,
  scope = 'fullscreen',
}: SplashScreenProps) {
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Safety timeout: force fade-out if config takes too long
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isFadingOut) {
        setIsFadingOut(true);
      }
    }, maxDuration);

    return () => clearTimeout(timer);
  }, [maxDuration, isFadingOut]);

  // Start fade-out when ready
  useEffect(() => {
    if (isReady && !isFadingOut) {
      // Small delay to ensure theme is applied before fade starts
      const timer = setTimeout(() => {
        setIsFadingOut(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isReady, isFadingOut]);

  // Remove from DOM after fade-out animation completes
  useEffect(() => {
    if (isFadingOut) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        onFinished();
      }, 600); // match CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isFadingOut, onFinished]);

  if (!isVisible) return null;

  return (
    <div
      className={`splash-screen ${scope === 'container' ? 'splash-screen--container' : ''} ${isFadingOut ? 'splash-fade-out' : ''}`}
      aria-label="Loading Vault Play"
    >
      {/* Animated background shimmer */}
      <div className="splash-bg-shimmer" />

      {/* Floating sparkle particles */}
      <div className="splash-particles">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="splash-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
              width: `${2 + Math.random() * 4}px`,
              height: `${2 + Math.random() * 4}px`,
            }}
          />
        ))}
      </div>

      {/* Center content */}
      <div className="splash-content">
        {/* Logo glow ring */}
        <div className="splash-logo-ring">
          <div className="splash-logo-inner">
            {/* The app name is the heading right below, so the logo is decorative. */}
            <img className="splash-logo-mark" src={vaultPlayLogo} alt="" draggable={false} />
          </div>
        </div>

        {/* App name */}
        <h1 className="splash-title">
          <span className="splash-title-kiosk">VAULT</span>
          <span className="splash-title-gaming">PLAY</span>
        </h1>

        {/* Loading dots */}
        <div className="splash-loading-dots">
          <div className="splash-dot" style={{ animationDelay: '0s' }} />
          <div className="splash-dot" style={{ animationDelay: '0.2s' }} />
          <div className="splash-dot" style={{ animationDelay: '0.4s' }} />
        </div>
      </div>

      {/* Inline styles - loaded immediately, no external deps */}
      <style>{`
        .splash-screen {
          position: fixed;
          inset: 0;
          z-index: var(--k-z-splash);
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--k-base);
          opacity: 1;
          transition: opacity 0.6s ease-out;
          overflow: hidden;
        }

        .splash-screen--container {
          position: absolute;
          inset: 0;
          z-index: 50;
        }

        .splash-fade-out {
          opacity: 0;
          pointer-events: none;
        }

        /* Animated background shimmer */
        .splash-bg-shimmer {
          position: absolute;
          inset: -50%;
          background: radial-gradient(
            ellipse at 50% 50%,
            color-mix(in srgb, var(--k-tint-glow) 22%, transparent) 0%,
            color-mix(in srgb, var(--k-tint-glow) 10%, transparent) 30%,
            transparent 70%
          );
          animation: splash-shimmer 4s ease-in-out infinite;
        }

        @keyframes splash-shimmer {
          0%, 100% {
            transform: scale(1) rotate(0deg);
            opacity: 0.6;
          }
          50% {
            transform: scale(1.2) rotate(3deg);
            opacity: 1;
          }
        }

        /* Floating particles */
        .splash-particles {
          position: absolute;
          inset: 0;
          overflow: hidden;
        }

        .splash-particle {
          position: absolute;
          background: radial-gradient(circle, color-mix(in srgb, var(--k-action-primary) 80%, transparent), transparent);
          border-radius: 50%;
          animation: splash-float linear infinite;
          opacity: 0;
        }

        @keyframes splash-float {
          0% {
            opacity: 0;
            transform: translateY(0) scale(0);
          }
          20% {
            opacity: 0.8;
            transform: translateY(-20px) scale(1);
          }
          80% {
            opacity: 0.4;
          }
          100% {
            opacity: 0;
            transform: translateY(-120px) scale(0.3);
          }
        }

        /* Center content */
        .splash-content {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
        }

        /* Logo glow ring */
        .splash-logo-ring {
          width: 6.25rem;
          height: 6.25rem;
          border-radius: 50%;
          background: conic-gradient(
            from 0deg,
            color-mix(in srgb, var(--k-info) 70%, transparent),
            color-mix(in srgb, var(--k-action-primary) 20%, transparent),
            color-mix(in srgb, var(--k-action-primary) 70%, transparent),
            color-mix(in srgb, var(--k-info) 20%, transparent),
            color-mix(in srgb, var(--k-info) 70%, transparent)
          );
          display: flex;
          align-items: center;
          justify-content: center;
          animation: splash-ring-rotate 3s linear infinite;
          padding: 0.1875rem;
        }

        .splash-logo-inner {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: var(--k-base);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* The ring spins; the logo turns back at the same rate so it stays upright. */
        .splash-logo-mark {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          object-fit: cover;
          animation: splash-ring-rotate 3s linear infinite reverse, splash-glow 2s ease-in-out infinite;
          filter: drop-shadow(0 0 12px color-mix(in srgb, var(--k-action-primary) 40%, transparent));
        }

        @keyframes splash-ring-rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes splash-glow {
          0%, 100% {
            filter: drop-shadow(0 0 12px color-mix(in srgb, var(--k-action-primary) 40%, transparent));
          }
          50% {
            filter: drop-shadow(0 0 24px color-mix(in srgb, var(--k-action-primary) 80%, transparent));
          }
        }

        /* Title */
        .splash-title {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.125rem;
          margin: 0;
        }

        .splash-title-kiosk {
          font-family: var(--font-display);
          font-size: var(--fs-display);
          font-weight: 900;
          font-style: italic;
          letter-spacing: 0.2em;
          background: var(--k-beam);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0 0 8px color-mix(in srgb, var(--k-action-primary) 30%, transparent));
        }

        .splash-title-gaming {
          font-family: var(--font-ui);
          font-size: var(--fs-base);
          font-weight: 600;
          letter-spacing: 0.5em;
          color: var(--k-text-tertiary);
        }

        /* Loading dots */
        .splash-loading-dots {
          display: flex;
          gap: 0.5rem;
          margin-top: 0.75rem;
        }

        .splash-dot {
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 50%;
          background: color-mix(in srgb, var(--k-action-primary) 70%, transparent);
          animation: splash-dot-bounce 1.2s ease-in-out infinite;
        }

        @keyframes splash-dot-bounce {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.3;
          }
          30% {
            transform: translateY(-10px);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
